import React, { useState, useCallback } from "react";
import { generatePairingCode } from "../../api/whatsapp/ownerWhatsAppApi";
import {
  Modal,
  Button,
  Input,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const PairWhatsAppModal = ({ isOpen, onClose, onSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pairingCode, setPairingCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      toast.error("Phone number is required.");
      return;
    }
    setLoading(true);
    try {
      const res = await generatePairingCode(phoneNumber);
      if (res.success) {
        setPairingCode(res.pairingCode);
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate code.");
    } finally {
      setLoading(false);
    }
  };

  // Memoized so its identity stays stable across re-renders (e.g. every
  // keystroke in the phone input below). Modal no longer depends on onClose
  // for its focus-trap effect either, but keeping this stable is good
  // practice regardless — avoids the same class of bug wherever onClose
  // ends up consumed.
  const handleModalClose = useCallback(() => {
    setPairingCode(null);
    setPhoneNumber("");
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title="Link WhatsApp via Phone Number"
      footer={
        !pairingCode ? (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={handleModalClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="pair-whatsapp-form"
              variant="primary"
              isLoading={loading}
            >
              Get Pairing Code
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="primary"
            fullWidth
            onClick={handleModalClose}
          >
            Done / Close
          </Button>
        )
      }
    >
      <div className="space-y-4 pt-1">
        {!pairingCode ? (
          <form
            id="pair-whatsapp-form"
            onSubmit={handleRequestCode}
            className="space-y-4"
          >
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              Enter the phone number connected to your school's official WhatsApp account.
            </p>
            <Input
              label="Phone Number (with country code)"
              type="text"
              placeholder="+91 9876543210"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
            />
          </form>
        ) : (
          <div className="space-y-4 text-center">
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              Open WhatsApp on your phone &rarr; <strong>Linked Devices</strong> &rarr; <strong>Link with Phone Number</strong>, and enter:
            </p>
            <div className="rounded-card border border-success-500/30 bg-success-50 p-6 font-mono text-3xl font-bold tracking-widest text-success-700 shadow-xs">
              {pairingCode}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PairWhatsAppModal;